/**
 * Las herramientas PROPIAS de Videos cortos para el agente del Chat
 * (ADR-2026-10-30-videos-cortos-con-el-agente). Mismo patrón que el Catálogo: viajan al modelo
 * sólo mientras la sesión está montada (`useRegistrarPantalla`) y se resuelven en `ejecutar.ts`
 * con las MISMAS acciones que usan los botones (`usePlanDeVideos`). Nada de esto va a
 * `V2_AGENT_TOOLS`. Los nombres de productos llevan "a_la_tanda" / "de_la_tanda" para no pisar
 * `sumar_productos` del Catálogo.
 */
import type { HerramientaDePantalla } from '../../../../../chat-atlas';
import { MAX_INDICACION, MAX_LOCUCION, MAX_PRODUCTOS, MAX_PROMPT } from '../plan';

export const CLAVE_PANTALLA_VIDEOS_CORTOS = 'modo:videos-cortos';
export const SKILL_DE_VIDEOS_CORTOS = 'videos-cortos';

const SOLO = {
  type: 'object',
  description: 'Qué productos (ids de PANTALLA ACTUAL). Sin esto, todos los que correspondan.',
  properties: { productos: { type: 'array', items: { type: 'string' } } },
};

export const HERRAMIENTAS_DE_VIDEOS_CORTOS: HerramientaDePantalla[] = [
  {
    name: 'escribir_guiones',
    etiqueta: 'Escribiendo los guiones…',
    description:
      'Manda al escritor del backend (el director de video de Atlas: mira las fotos de cada producto, piensa cómo se usa, escribe las tomas, el prompt y la locución) los productos sin guion, o los de `solo`. Un pedido por producto, en paralelo; tarda de 30 a 60 segundos por producto y NO cuesta créditos. ' +
      'Los guiones aparecen en las tarjetas a medida que llegan: no los repitas en el chat. Con `indicacion` aplica un cambio puntual a esta escritura ("más corto", "que se vea en una cocina"). Un producto YA ESCRITO no se reescribe salvo `aunque_esten_escritos: true`; uno fijado (editado a mano) sólo si además lo nombrás en `solo` (es lo que pide Reescribir). ' +
      'Escribe con o sin locución según la voz de cada producto (la del lote, o la propia): si la persona prende la voz después, los guiones sin locución quedan por reescribir y te lo dice PANTALLA ACTUAL. Devuelve cuántos salieron y cuáles fallaron (con el motivo).',
    input_schema: {
      type: 'object',
      properties: {
        solo: SOLO,
        indicacion: { type: 'string', description: `Un cambio puntual para ESTA escritura (hasta ${MAX_INDICACION}). No queda guardado: si tiene que valer siempre, va en configurar_tanda (indicacion) o en las notas del producto.` },
        aunque_esten_escritos: { type: 'boolean', description: 'true = reescribir también los que ya tienen guion (los de `solo`, o todos). Un video ya salido con guion nuevo se vuelve a filmar sólo si la persona lo pide (rehacer).' },
      },
    },
  },
  {
    name: 'ver_guiones',
    etiqueta: 'Mirando los guiones…',
    description:
      'Te muestra, de los productos de `solo` (sin `solo`, todos), el guion entero: la idea, las tomas en español, el PROMPT en inglés, la LOCUCIÓN, para quién es (público), la voz y el estado del video. Usala para leer un guion antes de editarlo con editar_guion, o para contarle a la persona qué va a decir un video. No cuesta créditos. Lo que mirás se arrastra en el historial: pedí sólo lo que hace falta.',
    input_schema: { type: 'object', properties: { solo: SOLO } },
  },
  {
    name: 'editar_guion',
    etiqueta: 'Cambiando un guion…',
    description:
      `Reemplaza el prompt (en inglés, con las tomas "Shot N (a-bs): …" y la frase "Physical realism:") y/o la locución (en rioplatense, con voseo, hasta ~33 palabras para que entre en 15 s) de UN producto, y lo deja FIJADO: el escritor no lo pisa. Leé antes el actual con ver_guiones y cambiá sólo lo pedido. Una locución vacía deja ese video sin voz. Para un cambio en muchos productos no es esto: escribir_guiones con \`solo\` e \`indicacion\`.`,
    input_schema: {
      type: 'object',
      properties: {
        producto_id: { type: 'string' },
        prompt: { type: 'string', description: `El prompt entero para el motor (hasta ${MAX_PROMPT}).` },
        locucion: { type: 'string', description: `El texto completo de la voz en off (hasta ${MAX_LOCUCION}). "" = sin voz en este video.` },
      },
      required: ['producto_id'],
    },
  },
  {
    name: 'elegir_voz',
    etiqueta: 'Eligiendo las voces…',
    description:
      'Asigna la voz en off a productos (o al lote entero, sin `producto_id`). `voz` acepta el id o el nombre de una voz de la lista de PANTALLA ACTUAL ("Malena"), `auto` (Atlas elige por producto según para quién es: voz de mujer, de hombre, o alterna) o `sin` (sólo música). ' +
      'Elegí por producto según su público, su rubro y el tono del video; en un lote grande alterná voces para que no suene todo igual. Preferí voces en español argentino: una en inglés lee el guion con acento. Un producto sin voz propia usa la del lote. Prender la voz sobre un guion sin locución lo deja por reescribir: después escribir_guiones.',
    input_schema: {
      type: 'object',
      properties: {
        asignaciones: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              producto_id: { type: 'string', description: 'El id del producto. Sin esto, es la voz del lote (la de todos los que no tienen una propia).' },
              voz: { type: 'string', description: 'Id o nombre de la voz, "auto" o "sin".' },
            },
            required: ['voz'],
          },
        },
      },
      required: ['asignaciones'],
    },
  },
  {
    name: 'configurar_tanda',
    etiqueta: 'Configurando el lote…',
    description:
      `Cambial lote entero: el título, los subtítulos (sólo tienen efecto con voz: siguen la voz palabra por palabra) y la indicación general (hasta ${MAX_INDICACION}: lo que el director tiene que saber para TODOS los videos; cambiarla no reescribe sola: después escribir_guiones con aunque_esten_escritos si la persona quiere aplicarla a lo ya escrito). También las notas de UN producto (lo que el director tiene que saber de ESE producto).`,
    input_schema: {
      type: 'object',
      properties: {
        titulo: { type: 'string', description: 'Nombre corto del lote ("Videos de la colección invierno").' },
        subtitulos: { type: 'boolean' },
        indicacion: { type: 'string', description: 'La indicación general. "" la saca.' },
        notas: {
          type: 'array',
          items: { type: 'object', properties: { producto_id: { type: 'string' }, notas: { type: 'string', description: 'Hasta 600. "" las saca.' } }, required: ['producto_id', 'notas'] },
        },
      },
    },
  },
  {
    name: 'sumar_productos_a_la_tanda',
    etiqueta: 'Sumando productos al lote…',
    description:
      `Suma al lote productos que YA están en el catálogo de la marca (un producto adjunto, uno que nombró, uno recién creado con agregar_productos). Van al final, sin guion: después llamá escribir_guiones con \`solo\`. Lo que ya estaba no se duplica. Hasta ${MAX_PRODUCTOS} productos por lote. Si mandó fotos de un producto que NO está en el catálogo, primero agregar_productos.`,
    input_schema: {
      type: 'object',
      properties: { product_ids: { type: 'array', items: { type: 'string' }, description: 'Los ids de los productos (los del adjunto o del catálogo).' } },
      required: ['product_ids'],
    },
  },
  {
    name: 'quitar_productos_de_la_tanda',
    etiqueta: 'Sacando productos del lote…',
    description: 'Saca productos del lote (su guion se va; un video ya salido queda en Creaciones). Un video que se está creando no se puede sacar: se termina y se cobra.',
    input_schema: {
      type: 'object',
      properties: { product_ids: { type: 'array', items: { type: 'string' } } },
      required: ['product_ids'],
    },
  },
  {
    name: 'crear_videos',
    etiqueta: 'Esperando que la persona toque Crear…',
    description:
      'SÓLO si la persona pidió crear los videos (o rehacer alguno). Valida que no falte nada, resalta el botón Crear con lo que se lanza y su costo (12 créditos por video; lo ya creado no se relanza salvo que se marque con `rehacer`, entero, o con `solo_voz`, 2 créditos: la locución o la voz nuevas sobre el mismo video, sin volver a filmarlo) y ESPERA su click (gasta créditos: el click es la confirmación). ' +
      'No la llames para saber el costo: está en PANTALLA ACTUAL. Devuelve cuántos videos se lanzaron o por qué no se pudo. Cada video tarda unos 5 minutos y salen varios a la vez; se ven en sus tarjetas.',
    input_schema: {
      type: 'object',
      properties: {
        rehacer: { type: 'array', items: { type: 'string' }, description: 'Ids de productos con video ya salido que la persona quiere volver a filmar con su guion (cuesta 12 cada uno).' },
        solo_voz: {
          type: 'array',
          items: { type: 'string' },
          description: 'Ids de productos con video ya salido a los que sólo les cambió la locución o la voz: se rehace sólo el audio y los subtítulos, sin filmar (2 créditos cada uno). Si cambió el prompt, va en `rehacer`.',
        },
      },
    },
  },
];

export const NOMBRES_HERRAMIENTAS_DE_VIDEOS_CORTOS: string[] = HERRAMIENTAS_DE_VIDEOS_CORTOS.map((h) => h.name);
